import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useAuthModal } from '../../contexts/AuthModalContext';
import { useToast } from '../../contexts/ToastContext';
import Modal from '../ui/Modal';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';

const AuthModal: React.FC = () => {
  const { t } = useTranslation();
  const { login, signup } = useAuth();
  const { isOpen, mode, closeModal, openSignup, openLogin } = useAuthModal();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await login(email, password);
      success(t('auth.loginSuccess', 'Welcome back!'));
      closeModal();
    } catch (err: any) {
      showError(err.message || t('auth.loginFailed', 'Login failed. Please try again.'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (userData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
  }) => {
    setIsLoading(true);
    try {
      await signup(userData);
      success(t('auth.signupSuccess', 'Account created successfully! Welcome!'));
      closeModal();
    } catch (err: any) {
      showError(err.message || t('auth.signupFailed', 'Registration failed. Please try again.'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      size="md"
      showCloseButton={true}
    >
      {mode === 'login' ? (
        <LoginForm
          onLogin={handleLogin}
          onSwitchToSignup={openSignup}
          loading={isLoading}
        />
      ) : (
        <SignupForm
          onSignup={handleSignup}
          onSwitchToLogin={openLogin}
          loading={isLoading}
        />
      )}
    </Modal>
  );
};

export default AuthModal;
